<template>
  <div v-if="alerts.length" class="alerts-chip-wrap">
    <button class="alerts-chip" :class="chipClass" @click="open = !open">
      <span v-if="hasCritical" class="alerts-chip__dot"></span>
      <span class="mdi mdi-alert-outline alerts-chip__icon"></span>
      <span class="alerts-chip__count">{{ alerts.length }}</span>
    </button>

    <div v-if="open" class="alerts-chip__backdrop" @click="open = false"></div>

    <div v-if="open" class="alerts-chip__dropdown">
      <div class="alerts-chip__header">
        <span class="alerts-chip__header-title">Alertas activas</span>
        <button class="alerts-chip__mark-all" @click="markAllSeen">Marcar todas como vistas</button>
      </div>

      <div class="alerts-chip__list">
        <AlertCard
          v-for="alert in sortedAlerts"
          :key="alert.key"
          v-bind="alert"
          @action="$emit('alert-action', alert)"
          @dismiss="$emit('alert-dismiss', alert)"
        />
      </div>

      <div class="alerts-chip__footer">
        <button class="alerts-chip__view-all" @click="onViewAll">Ver todas las alertas</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import AlertCard from './AlertCard.vue'

const props = defineProps({
  alerts: { type: Array, default: () => [] },
})

const emit = defineEmits(['alert-action', 'alert-dismiss', 'view-all'])

const open = ref(false)

const hasCritical = computed(() => props.alerts.some((a) => a.severity === 'CRITICAL'))
const hasWarning = computed(() => props.alerts.some((a) => a.severity === 'WARNING'))

const chipClass = computed(() => {
  if (hasCritical.value) return 'alerts-chip--critical'
  if (hasWarning.value) return 'alerts-chip--warning'
  return 'alerts-chip--info'
})

const SEVERITY_RANK = { CRITICAL: 0, WARNING: 1, INFO: 2 }

// Severidad primero, y dentro del mismo nivel la más antigua primero
// (la que lleva más tiempo esperando es la más urgente de atender).
const sortedAlerts = computed(() => {
  return [...props.alerts].sort((a, b) => {
    const rankDiff = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
    if (rankDiff !== 0) return rankDiff
    return new Date(a.since).getTime() - new Date(b.since).getTime()
  })
})

function markAllSeen() {
  for (const alert of props.alerts) emit('alert-dismiss', alert)
  open.value = false
}

function onViewAll() {
  open.value = false
  emit('view-all')
}
</script>

<style scoped>
.alerts-chip-wrap {
  position: relative;
}

.alerts-chip {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  height: 32px;
  padding: 0 0.65rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid var(--border-active);
  border-radius: 6px;
  cursor: pointer;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--text-primary);
}

.alerts-chip--critical { border-color: #e84545; color: #e84545; }
.alerts-chip--warning { border-color: #f0a500; color: #f0a500; }
.alerts-chip--info { border-color: #3b82f6; color: #3b82f6; }

.alerts-chip__icon {
  font-size: 0.85rem;
}

.alerts-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: alerts-chip-blink 1.4s ease-in-out infinite;
}

@keyframes alerts-chip-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.alerts-chip__backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}

.alerts-chip__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 380px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 41;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.alerts-chip__header {
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.9rem;
  border-bottom: 1px solid #f1f5f9;
}

.alerts-chip__header-title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--text-content);
}

.alerts-chip__mark-all {
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 0.68rem;
  color: var(--accent-blue);
  padding: 0;
}

.alerts-chip__list {
  position: relative;
  max-height: 420px;
  overflow-y: auto;
  padding: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.alerts-chip__list::after {
  content: '';
  position: sticky;
  display: block;
  bottom: 0;
  left: 0;
  right: 0;
  height: 18px;
  margin-top: -18px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff);
  pointer-events: none;
}

.alerts-chip__footer {
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #f1f5f9;
}

.alerts-chip__view-all {
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--accent-blue);
}
</style>